<template>
    <div class="email-body">
        <div v-if="row.attachmentURL" class="audio-attachment">
            <audio
                class="mb-3"
                controls
                v-if="isAudio(row.attachmentURL)"
                :src="row.attachmentURL"
            ></audio>
        </div>

        <div v-if="attachments.length" class="email-attachments">
            <div class="email-attachments-label">
                <v-icon small class="mr-1">mdi-paperclip</v-icon>
                {{ attachments.length }}
                {{ attachments.length === 1 ? "attachment" : "attachments" }}
                <v-tooltip
                    v-if="showManageFiles && hasManageableFiles"
                    bottom
                    :disabled="hasLinkedAccount"
                >
                    <span slot="activator" class="manage-files-action ml-3">
                        <v-btn
                            small
                            color="success"
                            :disabled="!hasLinkedAccount"
                            @click.stop="openManageFiles"
                        >
                            Manage files
                        </v-btn>
                    </span>
                    <span>Link this communication to an account before managing files.</span>
                </v-tooltip>
            </div>
            <div class="email-attachment-list">
                <div
                    v-for="file in attachments"
                    :key="file.id"
                    class="email-attachment-thumb"
                    :title="file.fileName"
                    @click="openFile(file)"
                >
                    <div class="email-attachment-thumb-preview">
                        <img
                            v-if="fileKind(file) === 'image' && file.fileURL"
                            class="email-attachment-thumb-image"
                            :src="file.fileURL"
                            :alt="file.fileName"
                        />
                        <pdf
                            v-else-if="fileKind(file) === 'pdf' && file.fileURL"
                            class="email-attachment-thumb-pdf"
                            :src="file.fileURL"
                            :page="1"
                        ></pdf>
                        <v-icon v-else size="36" color="#90a4ae">
                            {{ fileIcon(file) }}
                        </v-icon>
                    </div>
                    <div class="email-attachment-thumb-name">
                        {{ file.fileName }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showBody && hasHtml" class="email-html-wrap">
            <iframe
                class="email-html-frame"
                sandbox="allow-same-origin allow-popups"
                :srcdoc="row.bodyHtml"
            ></iframe>
        </div>
        <div v-else-if="showBody && row.text" class="email-plain-text">{{ trimText(row.text) }}</div>

        <document-preview-dialog
            v-model="preview.open"
            :url="preview.url"
            :name="preview.name"
            :kind="preview.kind"
            :account-name="row && row.accountName"
            :accountID="row && row.accountID"
            :subject="row && row.subject"
            :received="row && row.date"
        ></document-preview-dialog>
    </div>
</template>
<script>
import pdf from "vue-pdf";

export default {
    components: {
        pdf: pdf,
    },
    inject: {
        openManageFilesDialog: {
            default: null,
        },
    },
    props: {
        row: Object,
        isIncoming: {
            type: Boolean,
            default: false,
        },
        columns: Array,
        reloadGrid: Function,
        showBody: {
            type: Boolean,
            default: true,
        },
        showManageFiles: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            attachments: [],
            preview: {
                open: false,
                url: "",
                name: "",
                kind: "",
            },
        };
    },
    watch: {
        rowId: function() {
            this.attachments = [];
            this.loadAttachments();
        },
    },
    computed: {
        rowId() {
            return this.row && this.row.id;
        },
        hasHtml() {
            const html = this.row && this.row.bodyHtml;
            return !!(html && html.trim() && /<[a-z][\s\S]*>/i.test(html));
        },
        hasManageableFiles() {
            return this.attachments.some((file) => this.isManageable(file));
        },
        hasLinkedAccount() {
            if (!this.row || this.row.needsAccount) {
                return false;
            }
            var id = (this.row.accountID || "").trim();
            return !!id;
        },
    },
    async created() {
        await this.loadAttachments();
    },
    methods: {
        isAudio(url) {
            return url && !!url.match(/\.(mp3|wav|wma)$/i);
        },
        trimText(text) {
            if (!text) return text;
            // white-space: pre-line turns leading/trailing newlines into
            // blank lines, so trim the ends (internal line breaks stay intact).
            return text.trimStart().trimEnd();
        },
        async loadAttachments() {
            if (!this.row || !this.row.id || !this.row.hasAttachments) {
                return;
            }

            try {
                const files = await this.$http.get(
                    "api/emails/getAttachments?emailID=" + this.row.id
                );
                this.attachments = files || [];
            } catch (e) {
                this.attachments = [];
            }
        },
        fileKind(file) {
            const name = ((file && file.fileName) || "").toLowerCase();
            const type = ((file && file.contentType) || "").toLowerCase();
            const ext = name.includes(".") ? name.split(".").pop() : "";

            if (type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) {
                return "image";
            }
            if (type === "application/pdf" || ext === "pdf") {
                return "pdf";
            }
            if (type.startsWith("audio/") || ["mp3", "wav", "wma", "ogg", "m4a"].includes(ext)) {
                return "audio";
            }
            if (type.startsWith("video/") || ["mp4", "webm", "mov"].includes(ext)) {
                return "video";
            }
            if (["txt", "html", "htm"].includes(ext) || type.startsWith("text/")) {
                return "other";
            }

            return "download";
        },
        canPreview(file) {
            return this.fileKind(file) !== "download";
        },
        isManageable(file) {
            const kind = this.fileKind(file);
            if (kind !== "pdf" && kind !== "image" || !this.isIncoming) {
                return false;
            }

            const name = ((file && file.fileName) || "").toLowerCase();
            const ext = name.includes(".") ? name.split(".").pop() : "";
            return ext !== "svg" && ext !== "webp";
        },
        openManageFiles() {
            if (!this.hasLinkedAccount || !this.row || !this.row.id) {
                return;
            }

            if (this.openManageFilesDialog) {
                this.openManageFilesDialog({
                    source: "email",
                    sourceId: this.row.id,
                    communicationID: this.row.communicationID,
                });
                return;
            }

            var route = this.$router.resolve({
                name: "DocumentSplitter",
                params: {
                    source: "email",
                    sourceId: String(this.row.id),
                },
            });
            window.open(route.href, "_blank");
        },
        fileIcon(file) {
            const kind = this.fileKind(file);
            if (kind === "image") return "mdi-file-image";
            if (kind === "pdf") return "mdi-file-pdf";
            if (kind === "audio") return "mdi-file-music";
            if (kind === "video") return "mdi-file-video";
            return "mdi-file";
        },
        openFile(file) {
            const kind = this.fileKind(file);
            const url = file.fileURL;

            if (!url) {
                return;
            }

            if (kind === "download") {
                const link = document.createElement("a");
                link.href = url;
                link.target = "_blank";
                link.rel = "noopener";
                link.download = file.fileName || "attachment";
                link.click();
                return;
            }

            this.preview = {
                open: true,
                url,
                name: file.fileName || "Attachment",
                kind,
            };
        },
    },
};
</script>
<style>
.email-body {
    max-width: 1000px;
    padding: 0 4px 8px 16px;
}

.email-attachments {
    margin-bottom: 12px;
}

.email-attachments-label {
    color: #607d8b;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
}

.manage-files-action {
    display: inline-block;
}

.email-attachment-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.email-attachment-thumb {
    width: 88px;
    cursor: pointer;
}

.email-attachment-thumb-preview {
    width: 88px;
    height: 110px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background: #f7f8f7;
    display: flex;
    align-items: center;
    justify-content: center;
}

.email-attachment-thumb:hover .email-attachment-thumb-preview {
    border-color: #4caf50;
}

.email-attachment-thumb-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
}

.email-attachment-thumb-pdf {
    display: block;
    width: 100%;
    pointer-events: none;
}

.email-attachment-thumb-pdf canvas {
    width: 100% !important;
    height: auto !important;
}

.email-attachment-thumb-pdf .annotationLayer,
.email-attachment-thumb-pdf .annotationLayer a {
    pointer-events: none !important;
}

.email-attachment-thumb-name {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.25;
    color: #546e7a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.email-html-wrap {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;
}

.email-html-frame {
    width: 100%;
    min-height: 220px;
    height: 480px;
    border: 0;
    background: #fff;
}

.email-plain-text {
    white-space: pre-line;
    line-height: 1.45;
}

.email-preview-body {
    background: #111;
    min-height: 200px;
}

.email-preview-image,
.email-preview-video {
    display: block;
    max-width: 100%;
    max-height: 80vh;
    margin: 0 auto;
}

.email-preview-frame {
    width: 100%;
    height: 75vh;
    border: 0;
    background: #fff;
}
</style>
